import { useState } from 'react';
import testimonialsData from '@/data/testimonials.json';
import type { TestimonialsData } from '@/data/types';

const data: TestimonialsData = testimonialsData;

export function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentTestimonial = data.testimonials[currentIndex];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % data.testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + data.testimonials.length) % data.testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span
        key={i}
        className={`text-lg ${i < rating ? 'text-blue-600' : 'text-gray-300'}`}
      >
        ★
      </span>
    ));
  };

  return (
    <section className="py-16 bg-gray-50 relative overflow-hidden">
      {/* Background yacht illustration */}
      <div className="absolute left-0 top-1/2 transform -translate-y-1/2 opacity-10">
        <svg
          width="240"
          height="120"
          viewBox="0 0 240 120"
          fill="none"
          className="text-gray-400"
        >
          <path
            d="M20 80 L220 80 L200 40 L40 40 Z M80 40 L80 20 M120 40 L120 20 M160 40 L160 20 M200 40 L200 20"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
          <circle cx="60" cy="90" r="3" fill="currentColor" />
          <circle cx="100" cy="90" r="3" fill="currentColor" />
          <circle cx="140" cy="90" r="3" fill="currentColor" />
          <circle cx="180" cy="90" r="3" fill="currentColor" />
        </svg>
      </div>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-teal-600 mb-2 uppercase tracking-wider">
            {data.title}
          </h2>
          <p className="text-gray-600 text-sm uppercase tracking-widest">
            {data.subtitle}
          </p>
        </div>

        {/* Testimonial Content */}
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8 min-h-[280px] md:min-h-[240px] flex flex-col justify-center">
            <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6">
              {currentTestimonial.title}
            </h3>
            <p className="text-gray-700 text-lg leading-relaxed mb-6 max-w-3xl mx-auto">
              {currentTestimonial.content}
            </p>
            <p className="text-gray-600 italic mb-2">
              {currentTestimonial.author}
            </p>
          </div>

          {/* User Avatar */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Customer Name */}
          <div className="mb-4">
            <p className="font-semibold text-gray-800 text-lg">
              {currentTestimonial.author}
            </p>
          </div>

          {/* Star Rating */}
          <div className="flex justify-center mb-8">
            {renderStars(currentTestimonial.rating)}
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center space-x-2">
            {data.testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                  index === currentIndex ? 'bg-blue-600' : 'bg-gray-300'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          {/* Navigation Arrows (Hidden on mobile, shown on larger screens) */}
          <div className="hidden md:block">
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center hover:bg-gray-50 transition-colors"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center hover:bg-gray-50 transition-colors"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}